<template>
  <div
    v-if="objectUuid"
    class="form tab-content scroll-fire-fox"
    id="form-splicing-tab"
    style="overflow: auto; min-height: 320px;"
    @click="closeInsidePlanMenu"
  >
    <v-layout  column ref="splicingTab" style="height: 100%; max-height: 62vh">
      <v-layout align-center style="height: 100%; min-height: 50px;">
        <v-flex xs4 style="padding-left: 25px;" justify-start>
          <object-options
            :object-endpoint="objectEndpoint"
            :object-uuid="objectUuid"
            side="origin"
            :input-label="$tc('Origin', 1)"
            :selection="origin.selectedObjects"
            :cables="origin.cables"
            :splitters="splitters"
            placeholder="Origin"
            :action-type="actionType"
            :disabled="visibleData === 'insidePlan'"
            @set-cables="origin.cables = $event"
            @select-objects="selectObjects('origin', $event)"
          />
        </v-flex>

        <v-flex xs4 align-center>
          <toolbar
            :loading="loading"
            :disabled="disabled"
            :visible-data="visibleData"
            :has-inside-plan-button="objectEndpoint === 'closures'"
            @open-table="openTable"
            @switch-visible-data="switchVisibleData"
          />
        </v-flex>

        <v-flex xs4 class="d-flex justify-end" style="padding-right: 25px;">
          <object-options
            :loading="loading"
            :object-endpoint="objectEndpoint"
            :object-uuid="objectUuid"
            side="destination"
            :input-label="$tc('Destination', 1)"
            :selection="destination.selectedObjects"
            :cables="destination.cables"
            :splitters="splitters"
            :action-type="actionType"
            placeholder="Destination"
            :disabled="visibleData === 'insidePlan'"
            @set-cables="destination.cables = $event"
            @select-objects="selectObjects('destination', $event)"
          />
        </v-flex>
      </v-layout>
      <splitters-list
        v-if="splittersList.length > 0"
        :list="splittersList"
        :fiber-colors="fiberColours"
        :cables="[...destination.cables, ...origin.cables]"
        :splitters="splitters"
        @open-menu="listShowMenu"
      />
      <transition name="fadeInsidePlan" mode="out-in">
        <v-layout
          v-if="visibleData === 'splicing'"
          class="mt-4 pl-3 pr-2 splicing-diagram-container"
          style="height: 100%; position: relative; justify-content: space-between;"
          :id="'splicing-connections-container-' + objectUuid"
        >
          <v-flex class="splicing-diagram-origin-container" xs4 >
            <div v-for="(object, index) in origin.selectedObjects" :key="index" class="splicing-origin-table">
              <component
                :is="getObjectEntriesComponent(object)"
                side="origin"
                :object="object"
                :opposite-objects="destination.selectedObjects"
                :objects="origin.selectedObjects"
                :selected-item="origin.selectedConnectionPoint"
                :connections="connections"
                :update-counter="updateCounter"
                :disabled="disabled"
                :fibers-per-tube="getFibersPerTube(object)"
                @select-item="selectItem('origin', $event)"
                @open-menu="showMenu('origin', $event)"
              />
            </div>
          </v-flex>

          <v-flex :style="{width: originWidth, left: originPosition}" class="connections-table-container">
            <connections
              v-if="visible"
              :object-uuid="objectUuid"
              :origin-objects="[...origin.selectedObjectsCopy, ...originSplitters]"
              :destination-objects="[...destination.selectedObjectsCopy, ...destinationSplitters]"
              :connections="connections"
              :update-counter="updateCounter"
              :splitters="splitters"
              :is-tech-local="isTechLocal"
              :equipmentSet="objectSet"
              :selected-origin="origin.selectedConnectionPoint"
              :selected-destination="destination.selectedConnectionPoint"
              @select-item="selectItem"
              @open-menu="$event => connectionsShowMenu($event)"
              @set-splitter="setSplitterData"
              @clear-selected-origin="clearSelectedOrigin"
            />
          </v-flex>

          <v-flex class="splicing-diagram-destination-container"  xs4>
            <div
              v-for="(object, index) in destination.selectedObjects"
              :key="index"
              class="splicing-destination-table"
            >
              <component
                :is="getObjectEntriesComponent(object)"
                side="destination"
                :object="object"
                :opposite-objects="origin.selectedObjects"
                :objects="destination.selectedObjects"
                :selected-item="destination.selectedConnectionPoint"
                :connections="connections"
                :update-counter="updateCounter"
                :disabled="disabled"
                :destination="true"
                :fibers-per-tube="getFibersPerTube(object)"
                @select-item="selectItem('destination', $event)"
                @open-menu="showMenu('destination', $event)"
              />
            </div>
          </v-flex>

          <connection-object-menu
            :show-menu="menu.show"
            :x="menu.x"
            :y="menu.y"
            :state="menu.selected.state"
            :disabled="disabled"
            :is-fiber="canShowCircuit"
            :showConnectionOrChange="showConnectionOrChange"
            :side="menu.selected.side"
            :is-expand="isExpand"
            :is-tech-local="isTechLocal"
            :action-type="actionType"
            @close-menu="closeMenu"
            @remove-connections="showDeleteConnectionDialog = true"
            @inspect="inspectConnection"
            @show-circuit="showCircuit"
            @add-connection="creatingConnectionSameSide"
            @edit-splitter="startEditingItem"
            @connect-to="connectTo"
            @view-splitter="viewSplitter"
            @change-connection="changeSelection()"
            @collapse-list-splitter="collapseListSplitter"
            @hide-list-splitter="hideListSplitter"
            @delete-splitter="showDeleteSplitterDialog = true"
          />
        </v-layout>
      </transition>

      <transition name="fadeInsidePlan" mode="out-in">
        <v-layout
          v-if="visibleData === 'insidePlan'"
          class="inside-plan-container mt-4 pl-2 pr-2"
          :id="'inside-plan-container-' + objectUuid"
          :key="'inside-plan-container-' + objectUuid"
        >
          <selected-drawer
            :connections="allConnections"
            :cassettes="cassettes"
            :show-save-button="false"
            @open-tray-connection-menu="openTrayConnectionMenu"
            @open-tube-form="openAddTubeFiberForm"
            @open-cassette-form="openCassetteForm"
            @open-delete-cassette-dialog="openDeleteCassetteDialog"
          />
        </v-layout>
      </transition>
    </v-layout>

    <splitter-edition-form
      v-if="editionFormIsOpen"
      :open="editionFormIsOpen"
      :original-item="selectedSplitterData"
      @close="closeItemEdition"
      @edit="editItem"
    />

    <create-connection-form
      v-if="isCreateEquipmentFormOpen"
      :open="isCreateEquipmentFormOpen"
      :object-uuid="objectUuid"
      :is-splitter="menu.selected.state === 'splitter'"
      :origin-cables="origin.cables"
      :destination-cables="destination.cables"
      :connections="connections"
      :selected-connection-point="menu.selected.connectionPoints"
      :splitters="splitters"
      :object-endpoint="objectEndpoint"
      :object-id="objectId"
      :is-tech-local="isTechLocal"
      :side="menu.selected.side"
      :network-level="networkLevel"
      @add-splitter="addSplitter"
      @create-connection-splitter="createConnectionSplitter"
      @select-item="splitterSelectItem"
      @close-connect-to="closeConnectTo"
    />

    <app-dialog-box
      :open="showDeleteConnectionDialog"
      :title="`${$t('Remove')} ${$tc('Connection', 1)}`"
      :content="'Are you sure you want to continue with this action?'"
      :main-action-label="$t('No')"
      @mainAction="cancelDeleteConnection"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="confirmDeleteConnections"
    />
    <app-dialog-box
      :open="showDeleteSplitterDialog"
      :title="`${$t('Remove')} ${$tc('Equipment', 1)}`"
      :content="'Are you sure you want to continue with this action?'"
      :main-action-label="$t('No')"
      @mainAction="showDeleteSplitterDialog = false"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="removeSplitter"
    />

    <app-dialog-box
      :open="showPreviousConnectionsDialog"
      :title="$t('RemovePreviousSplicingConnections.Title')"
      :content="previousConnectionsDialogMessage"
      :main-action-label="$t('No')"
      @mainAction="cancelNewSingleConnection"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="continueCreatingConnections"
    />

    <app-dialog-box
      :open="showMultipleNewConnectionsDialog"
      :title="$t('MultipleNewSplicingConnections.Title')"
      :content="multipleNewConnectionsDialogMessage"
      :main-action-label="$t('No')"
      @mainAction="cancelNewMultipleConnection"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="continueCreatingConnections(true)"
    />

    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="accent"
      background-color="transparent"
    />

    <tray-connection-menu
      v-if="trayConnectionMenu.isOpen"
      :horizontal="menuHorizontalPostion"
      :vertical="menuVerticalPosition"
      @edit-fiber="openEditTubeFiberForm('fiber')"
      @edit-tube="openEditTubeFiberForm('tube')"
      @remove-fiber="openDeleteTubeFiberDialog('fiber')"
      @remove-tube="openDeleteTubeFiberDialog('tube')"
    />
     <add-tube-fiber-form
      v-else-if="addTubeFiberForm.isOpen"
      :dialog-is-open="addTubeFiberForm.isOpen"
      :available-fibers="availableFibers(origin.cables, destination.cables)"
      @add-object="addTubeFiber"
      @close-form="closeAddTubeFiberForm"
    />
    <edit-tube-fiber-form
      v-else-if="editTubeFiberForm.isOpen"
      :dialog-is-open="editTubeFiberForm.isOpen"
      :fiber-type-options="fiberTypeOptions"
      :object-data="editTubeFiberForm.fiberData"
      :object-type="editTubeFiberForm.objectTypeToEdit"
      @edit-object="editTubeFiber"
      @close-form="closeEditTubeFiberForm"
    />
    <delete-tube-fiber-dialog
      v-else-if="tubeFiberDialog.isOpen"
      :dialog-is-open="tubeFiberDialog.isOpen"
      @cancel-delete="closeTubeFiberDialog"
      @confirm-delete="deleteTubeFiber"
    />
    <delete-cassette-dialog
      v-else-if="deleteCassetteDialog.isOpen"
      :dialog-is-open="deleteCassetteDialog.isOpen"
      :cassette-label="deleteCassetteDialog.cassetteLabel"
      @cancel-delete="closeDeleteCassetteDialog"
      @confirm-delete="confirmDeleteCassette"
    />
    <add-or-edit-cassette-form
      v-if="cassetteForm.isOpen"
      :dialog-is-open="cassetteForm.isOpen"
      :item-to-edit="objectToEdit"
      :cassette-options="cassetteMaterialsOptions"
      @create-cassette="saveCassette"
      @edit-cassette="saveCassette"
      @close-cassette-form="closeCassetteForm"
    />
  </div>
</template>

<script>
import SplitterEditionForm from "@/components/Home/Forms/_SpecialTabs/Splitters/SplitterEditionForm";
import { mapActions } from "vuex";
import LoadingSpinner from "vue-loading-overlay";
import ObjectOptions from "@/components/Home/Forms/_SpecialTabs/Splicing/ObjectOptions";
import CableFibers from "@/components/Home/Forms/_SpecialTabs/Splicing/CableFibers";
import Connections from "@/components/Home/Forms/_SpecialTabs/Splicing/Connections";
import CreateConnectionForm from "@/components/Home/Forms/_SpecialTabs/Splicing/CreateConnectionForm";
import Toolbar from "@/components/Home/Forms/_SpecialTabs/Splicing/Toolbar";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import SplittersList from "./SplittersInfo/SplittersList.vue";
import _ from "lodash";
import ConnectionObjectMenu from "@/components/Home/Forms/_SpecialTabs/Splicing/ObjectTable/ConnectionObjectMenu";
import SelectedDrawer from "@/components/Home/Content/Tabs/InsidePlan/SelectedDrawer.vue";
import Drawer from "@/components/Home/Content/Tabs/InsidePlan/Drawer.vue";
import { bus } from "@/main";
import TrayConnectionMenu from "@/components/Home/Content/Tabs/InsidePlan/menus/TrayConnectionMenu.vue";
import AddTubeFiberForm from "@/components/Home/Content/Tabs/InsidePlan/forms/AddTubeFiberForm.vue";
import EditTubeFiberForm from "@/components/Home/Content/Tabs/InsidePlan/forms/EditTubeFiberForm.vue";
import DeleteTubeFiberDialog from "@/components/Home/Content/Tabs/InsidePlan/forms/DeleteTubeFiberDialog.vue";
import DeleteCassetteDialog from "@/components/Home/Content/Tabs/InsidePlan/forms/DeleteCassetteDialog.vue";
import AddOrEditCassetteForm from "@/components/Home/Content/Tabs/InsidePlan/forms/AddOrEditCassetteForm.vue";
import { insidePlanMixin } from "@/mixins/insidePlanMixin";

export default {
  name: "ClosureFormSplicingTab",

  components: {
    ConnectionObjectMenu,
    SplitterEditionForm,
    ObjectOptions,
    AppDialogBox,
    Toolbar,
    Connections,
    CableFibers,
    LoadingSpinner,
    CreateConnectionForm,
    SplittersList,
    SelectedDrawer,
    TrayConnectionMenu,
    AddTubeFiberForm,
    EditTubeFiberForm,
    DeleteTubeFiberDialog,
    DeleteCassetteDialog,
    AddOrEditCassetteForm,
    Drawer,
  },

  mixins: [
    insidePlanMixin
  ],

  props: {
    objectEndpoint: {
      type: String
    },

    objectUuid: {
      type: String
    },

    objectId: {
      type: Number
    },

    visible: {
      type: Boolean
    },

    formSizeUpdate: Number,

    disabled: Boolean,

    isTechLocal: {
      type: Boolean,
      default: false
    },

    actionType: {
      type: String
    },
    networkLevel: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      originPosition: '216px',
      originWidth: '255px',
      origin: {
        cables: null,
        selectedObjects: [],
        selectedObjectsCopy: [],
        selectedConnectionPoint: null
      },
      isCreateEquipmentFormOpen: false,
      destination: {
        cables: null,
        selectedObjects: [],
        selectedObjectsCopy: [],
        selectedConnectionPoint: null
      },

      newConnections: {
        originItems: [],
        destinationItems: [],
        firstSideSelected: null
      },

      objectSet: null,
      selectedSplitterData: null,
      splitters: null,
      originSplitters : [],
      destinationSplitters: [],
      connections: null,
      initialSplicingConnections: [],
      previousConnectionsToRemove: [],
      showPreviousConnectionsDialog: false,
      showMultipleNewConnectionsDialog: false,

      updateCounter: 0,

      menu: {
        show: false,
        x: 0,
        y: 0,
        selected: {
          side: null,
          connectionPoints: null,
          state: null,
          prefix: null
        }
      },
      editionFormIsOpen:false,
      showConnectionOrChange: false,

      connectionSameSide: {
        side: null,
        item: null
      },
      showDeleteConnectionDialog: false,
      showDeleteSplitterDialog: false,
      fiberIsOrigin: false,
      splittersList: [],
      selectedSplitter: null,
      isExpand: false,
      visibleData: 'splicing',
      isconnectingTubes: false,
      cassettes: null,
    };
  },
  computed: {
    bothSidesEntriesAreSelected() {
      return (
        this.origin.selectedConnectionPoint &&
        this.destination.selectedConnectionPoint
      );
    },

    previousConnectionsDialogMessage() {
      return (
        this.$i18n.t(
          "RemovePreviousSplicingConnections.PreviousConnectionWarning"
        ) +
        "\n\n" +
        this.$i18n.t("RemovePreviousSplicingConnections.ConsequenceWarning") +
        "\n\n" +
        this.$i18n.t("ContinueConfirmation")
      );
    },

    multipleNewConnectionsDialogMessage() {
      return (
        this.$i18n.t("MultipleNewSplicingConnections.Message") +
        "\n\n" +
        this.$i18n.t("ContinueConfirmation")
      );
    },

    canShowCircuit() {
      return (
        this.menu.selected.prefix === "fb" ||
        (this.menu.selected.prefix === "pt" &&
          this.menu.selected.connectionPoints &&
          (!!this.menu.selected.connectionPoints[0].connectedFiber ||
            !!this.menu.selected.connectionPoints[0].connectedPort))
      );
    },
  },

  watch: {
    objectUuid(updatedUUID) {
      if (updatedUUID) {
        this.getAllConnections();
        window.localStorage.setItem('closureUuid', updatedUUID);
      }
    },

    bothSidesEntriesAreSelected(bothAreSelected) {
      if (bothAreSelected) {
        this.startCreatingConnection();
      }
    },

    formSizeUpdate() {
      this.onResizing();
      this.updateCounter++;
    },
    'origin.selectedObjects'(value){
      const lists = this.getCablesAndSplitters(value);
      this.origin.selectedObjectsCopy = lists.cables;
      this.destinationSplitters = lists.splitters;

    },
    'destination.selectedObjects'(value){
      const lists = this.getCablesAndSplitters(value);
      this.destination.selectedObjectsCopy = lists.cables;
      this.originSplitters = lists.splitters;
    },

    "origin.cables"() {
      this.addOriginFibersToFibersAndPortsList();
    },

    "destination.cables"() {
      this.addDestinationFibersToFibersAndPortsList();
    },

    splitters() {
      this.addSplittersPortsToFibersAndPortsList();
    },
  },

  mounted() {
    this.getClosureCassettes();
    // this.joinFibersFromAllCablesAndSplitters();
    window.localStorage.setItem('techLocal', this.isTechLocal.toString())
    this.getObjectSplitters();
    if (this.objectUuid) {
      this.getAllConnections();
      window.localStorage.setItem('closureUuid', this.objectUuid);
    }
  },

  methods: {
    ...mapActions([
      "fetchObjectSplicingConnections",
      "GET_ALL_LISTS",
      "deleteSplitter",
      "fetchSplittersInObject",
      "updateSplitter",
      "fetchClosureCassettes",
      "editCassette",
      "createCassette",
    ]),

    onResizing() {
      //splicing diagram div
      const diagramContainer = document.getElementsByClassName("splicing-diagram-container");
      const diagramContainerWidth = diagramContainer[0].getBoundingClientRect().width;
      //origin table div
      const diagramOriginContainer = document.getElementsByClassName("splicing-diagram-origin-container");
      const diagramOriginContainerWidth = diagramOriginContainer[0].getBoundingClientRect().width;
      //set position of connection container
      const connectionPosition = diagramOriginContainerWidth - 4;
      this.originPosition = `${connectionPosition}px`;
      //set width of connection container
      const connectionContainerWidth = (diagramContainerWidth - (diagramOriginContainerWidth*2)) + 12;
      this.originWidth = `${connectionContainerWidth}px`;
    },
    getCablesAndSplitters(lists){
      const cables = [];
      const splitters = [];
      lists.forEach(item => {
        if(this.isCable(item)){
          cables.push({...item});
        }else{
          splitters.push({...item});
        }
      });
      return {cables, splitters}
    },
    getFibersPerTube(cable){
      if (this.isCable(cable)){
        const fiberPerTubeCache = {};
        const tubes = [];
        cable.fibers.forEach(fiber => {
          if(!tubes.includes(fiber.fb_nr_tube)){
            tubes.push(fiber.fb_nr_tube);
          }
        });
        tubes.forEach(tube =>{
          let count = 0;
          cable.fibers.forEach(fiber =>{
            if(tube === fiber.fb_nr_tube){
              count++;
            }
          });
          fiberPerTubeCache[tube]= count;
        });
        return fiberPerTubeCache
      }
    },
    async getObjectSplitters() {
      try {
        const splittersOptions = await this.fetchSplittersInObject({
          objectEndpoint: this.objectEndpoint,
          objectUUID: this.objectUuid
        });
        this.splitters= _.orderBy(splittersOptions, ["sp_label", "sp_id"], ["asc", "asc"])
      } catch (e) {
        console.log(e);
      }
    },

    async getAllConnections() {
      this.loading = true;
      try {
        const allConnections = await this.fetchObjectSplicingConnections({
          objectEndpoint: this.objectEndpoint,
          objectUUID: this.objectUuid
        });
        this.initialSplicingConnections = [...allConnections];
        this.connections = [...allConnections];
        this.createConnectionsArray(allConnections);
        this.updateCounter++;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    getObjectEntriesComponent(object) {
      if (this.isCable(object)) {
        return "CableFibers";
      }
      return null;
    },

    selectObjects(side, selection) {
      if (
        this[side].selectedConnectionPoint &&
        !this.isConnectionPointParentSelected(side)
      ) {
        this[side].selectedConnectionPoint = null;
      }
      this[side].selectedObjects = [...selection.cables, ...selection.splitters];
      this.updateCounter++;
      this.$nextTick(() => {
        this.$emit("update-form-height");
      });
    },
    isConnectionPointParentSelected(side) {
      let connectionPoint = this[side].selectedConnectionPoint;
      if (connectionPoint) {
        if (this.isFiber(connectionPoint)) {
          let parent = this[side].selectedObjects.find(
            cable =>
              this.isCable(cable) && cable.cb_id === connectionPoint.fb_cable_id
          );
          return !!parent;
        } else if (this.isPort(connectionPoint)) {
          let parent = this[side].selectedObjects.find(
            splitter =>
              this.isSplitter(splitter) &&
              splitter.sp_id === connectionPoint.pt_splitter_id
          );
          return !!parent;
        }
      }
      return false;
    },
    clearPortObject(port){
      const {connectedFiber, connectedPort, connectionUuid, type, ...rest} = port;
      return rest;
    },
    async addSplitter(splitter){
      this.splitters.push(splitter);
    },
    removeConnectionFromList(fbId){
      this.splittersList.forEach(list => {
        list.waterfall.forEach(item => {
          item.splitter.inPorts.forEach(port => {
            if(port.connectedFiber && port.connectedFiber.fb_id === fbId){
              port.connectedFiber = null;
              return;
            }
          })
          item.splitter.outPorts.forEach(port => {
            if(port.connectedFiber && port.connectedFiber.fb_id === fbId){
              port.connectedFiber = null;
              return;
            }
          })
        });
      })

    },
    addConnectionToList(fiberOrPort, port, isInput){
      const cleanFiberOrPort = this.clearPortObject(fiberOrPort);
      const copy = [...this.splittersList];
      copy.forEach(list => {
        list.waterfall.forEach(obj => {
          if(obj.sp_id === port.pt_splitter_id){
            if(isInput){
              obj.splitter.inPorts.forEach(item => {
                if(item.pt_id === port.pt_id){
                  if(this.isFiber(fiberOrPort)){
                    item.connectedFiber = {...cleanFiberOrPort}
                  }else{
                    item.connectedPort = {...cleanFiberOrPort}
                  }
                  return
                }
              });
            }else{
              obj.splitter.outPorts.forEach(item => {
                if(item.pt_id === port.pt_id){
                  if(item.pt_id === port.pt_id){
                    if(this.isFiber(fiberOrPort)){
                      item.connectedFiber = {...cleanFiberOrPort}
                    }else{
                      item.connectedPort = {...cleanFiberOrPort}
                    }
                    return
                  }
                }
              });
            }
          }
        })
      })
      this.splittersList= [...copy];
    },
    createConnectionSplitter(fiberOrPort, port, isInput){
      if(fiberOrPort.type === 'port'){
        const cleanPort = this.clearPortObject(port);
        const cleanFiberOrPort = this.clearPortObject(fiberOrPort);
        if(isInput){
          this.splitters.forEach(splitter => {
            if(splitter.sp_id === port.pt_splitter_id){
              splitter.inPorts.forEach(item => {
                if(item.pt_id === port.pt_id){
                  item.connectedPort = {...cleanFiberOrPort};
                }
              })
            }else if(splitter.sp_id === fiberOrPort.pt_splitter_id){
              splitter.outPorts.forEach(item => {
                if(item.pt_id === fiberOrPort.pt_id){
                  item.connectedPort = {...cleanPort};
                }
              })
            }
          })
        }else{
          this.splitters.forEach(splitter => {
            if(splitter.sp_id === port.pt_splitter_id){
              splitter.outPorts.forEach(item => {
                if(item.pt_id === port.pt_id){
                  item.connectedPort = {...cleanFiberOrPort};
                }
              })
            }else if(splitter.sp_id === fiberOrPort.pt_splitter_id){
              splitter.inPorts.forEach(item => {
                if(item.pt_id === fiberOrPort.pt_id){
                  item.connectedPort = {...cleanPort};
                }
              })
            }
          })
        }
      }else{
        const cleanFiberOrPort = this.clearPortObject(fiberOrPort);
        if(isInput){
          this.splitters.forEach(splitter => {
            if(splitter.sp_id === port.pt_splitter_id){
              splitter.inPorts.forEach(item => {
                if(item.pt_id === port.pt_id){
                  item.connectedFiber = {...cleanFiberOrPort};
                }
              })
            }
          });
        }else{
          this.splitters.forEach(splitter => {
            if(splitter.sp_id === port.pt_splitter_id){
              splitter.outPorts.forEach(item => {
                if(item.pt_id === port.pt_id){
                  item.connectedFiber = {...cleanFiberOrPort};
                }
              })
            }
          });
        }
      }
      if(this.splittersList.length > 0){
        //add connection to list of splitters
        this.addConnectionToList(fiberOrPort, port, isInput);
      }
      //clear variables before create connection from connection form
      this.origin.selectedConnectionPoint = null;
      this.destination.selectedConnectionPoint = null;
    },
    splitterSelectItem(side, item, isOrigin, hasChange = false) {
      this.fiberIsOrigin = isOrigin;
      if (
        this[side].selectedConnectionPoint &&
        this.isSelectedItemEqual(side, item)
      ) {
        this[side].selectedConnectionPoint = null;
        document.getElementById("form-splicing-tab").onclick = "";
      } else {
        if (
          item.type === "fiber" &&
          !!this[side].selectedConnectionPoint &&
          this[side].selectedConnectionPoint.type === "fiber" &&
          this[side].selectedConnectionPoint.fb_uuid !== item.fb_uuid &&
          !hasChange
        ) {
          document.getElementById("form-splicing-tab").onclick = e => {
            this.showConnectionOrChange = true;
            this.connectionSameSide = { side, item };
            this.showMenu(side, { event: e, item });
          };
        } else {
          document.getElementById("form-splicing-tab").onclick = "";
          this[side].selectedConnectionPoint = item;
          let oppositeSide = this.getOppositeSide(side);
          if (this[oppositeSide].selectedConnectionPoint === null) {
            this.newConnections.firstSideSelected = side;
          }
        }
      }
      this.showConnectionOrChange = false;
      if(item.type === "port"){
        const options = [...this.splitters];
        let selectedObject = this[this.getOppositeSide(side)].selectedObjects.find(
          obj => obj.sp_id === item.pt_splitter_id
        );
        if (!selectedObject) {
          let objectToAdd = options.find(
            option => option.sp_id === item.pt_splitter_id
          );
          this[this.getOppositeSide(side)].selectedObjects.push(objectToAdd);
        }
      }
    },
    clearSelectedOrigin(){
      this.origin.selectedConnectionPoint = null;
    },
    selectItem(side, item, hasChange = false) {
      if(this.fiberIsOrigin){
        this.fiberIsOrigin = false;
      }
      if (
        this[side].selectedConnectionPoint &&
        this.isSelectedItemEqual(side, item)
      ) {
        this[side].selectedConnectionPoint = null;
        document.getElementById("form-splicing-tab").onclick = "";
      } else {
        if (
          item.type === "fiber" &&
          !!this[side].selectedConnectionPoint &&
          this[side].selectedConnectionPoint.type === "fiber" &&
          this[side].selectedConnectionPoint.fb_uuid !== item.fb_uuid &&
          !hasChange
        ) {
          this.connectionSameSide = { side, item };
          document.getElementById("form-splicing-tab").onclick = e => {
            if(this.connectionSameSide.item){
              this.showConnectionOrChange = true;
              this.connectionSameSide = { side, item };
              this.showMenu(side, { event: e, item });
            }
          };
        } else {
          document.getElementById("form-splicing-tab").onclick = "";
          this[side].selectedConnectionPoint = item;
          let oppositeSide = this.getOppositeSide(side);
          if (this[oppositeSide].selectedConnectionPoint === null) {
            this.newConnections.firstSideSelected = side;
            //show destination connection when connecting splitter to fibers
            if(this[oppositeSide].selectedConnectionPoint && this[oppositeSide].selectedConnectionPoint.hasOwnProperty('sp_id')){
              const options = [...this.splitters];
              let selectedObject = this[side].selectedObjects.find(
                obj => obj.sp_id === this[oppositeSide].selectedConnectionPoint.sp_id
              );
              if (!selectedObject) {
                let objectToAdd = options.find(
                  option => option.sp_id === this[oppositeSide].selectedConnectionPoint.sp_id
                );
                this[side].selectedObjects.push(objectToAdd);
              }
            }
          }
        }
      }
      this.showConnectionOrChange = false;
    },
    viewSplitter(){
      const splitterId = this.menu.selected.connectionPoints.destination.connectionPoints[0].pt_splitter_id;
      const splitters = [];
      this.getSpilttersConnected(splitterId, splitters);
      //delete duplicates from array
      const jsonObject = splitters.map(JSON.stringify);
      const uniqueSet = new Set(jsonObject);
      const uniqueArray = Array.from(uniqueSet).map(JSON.parse);
      //get final list
      const list = this.checkSplitterOutports(uniqueArray);
      this.addToSplittersList(list);
    },
    addToSplittersList(list){
      const listId = list[0].sp_id;
      const isInList = this.splittersList.find(item => item.id === listId);
      if(!isInList){
        this.splittersList.push({
          id: listId,
          waterfall: [...list]
        });
      }
    },
    checkSplitterOutports(array){
      let list = [];
      const reversedArray = array.slice().reverse();
      switch (reversedArray.length) {
        case 1:
          const splitters1 = [];
          this.getSpilttersToRemove(reversedArray[0].sp_id, splitters1);
          //add nivel 1 splitter to list
          list.push({...reversedArray[0], nivel: 1});
          const splittersList = this.getSplittersFromLevelOne(reversedArray[0].sp_id, splitters1);
          //need to delete current splitter from list returned
          list = [...list, ...splittersList];
          // Sort the array by `nivel` in ascending order
          list.sort((a, b) => a.nivel - b.nivel);
          break;
        case 2:
          const splitters = [];
          //do not need to look for siblings in first splitter because first splitter cannot have siblings
          //add nivel 1 splitter to list
          list.push({...reversedArray[0], nivel: 1});
          //add nivel 2 splitter to list
          list.push({...reversedArray[1], nivel: 2});
          //get siblings of splitter
          const siblings = this.getSiblings(reversedArray[1].splitter, 2);
          //add siblings to splitter
          list = [...list, ...siblings];
          //get childrens of splitter
          this.getSpilttersToRemove(reversedArray[0].sp_id, splitters);
          //add possible level 3 splitters
          splitters.forEach(sp => {
            const isfound = list.find(item => item.sp_id === sp.sp_id);
            if(!isfound){
              const splitter = this.splitters.find(item => item.sp_id === sp.sp_id);
              list.push({
                sp_id: splitter.sp_id,
                splitter,
                hide: false,
                nivel: 3
              })
            }
          });
          // Sort the array by `nivel` in ascending order
          list.sort((a, b) => a.nivel - b.nivel);
          break;
        case 3:
          //add splitter to list
          reversedArray.forEach((item, index) => {
            list.push({...item, nivel: (index + 1)});
          })

          //get siblings of level 2 splitter
          const siblingsLevelTwo = this.getSiblings(reversedArray[1].splitter, 2);
          //add siblings to splitter
          list = [...list, ...siblingsLevelTwo];
          //get splitter to remove from level one so we can get level 3 splitters
          const splitters2 = [];
          this.getSpilttersToRemove(reversedArray[0].sp_id, splitters2);
          splitters2.forEach(sp => {
            const isfound = list.find(item => item.sp_id === sp.sp_id);
            if(!isfound){
              const splitter = this.splitters.find(item => item.sp_id === sp.sp_id);
              list.push({
                sp_id: splitter.sp_id,
                splitter,
                hide: false,
                nivel: 3
              })
            }
          });
          // Sort the array by `nivel` in ascending order
          list.sort((a, b) => a.nivel - b.nivel);
          break;
        default:
          break;
      }
      return list;
    },
    getSplittersFromLevelOne(spId, array){
      const list = [];
      array.forEach(item => {
        if(item.sp_id !== spId){
          const splitter = this.splitters.find(splitter => splitter.sp_id === item.sp_id);
          const isLevelTwo = splitter.inPorts.find(port => port.connectedPort && port.connectedPort.pt_splitter_id === spId)
          if(isLevelTwo){
            list.push({
              sp_id: splitter.sp_id,
              splitter,
              hide: false,
              nivel: 2
            })
          }else{
            list.push({
              sp_id: splitter.sp_id,
              splitter,
              hide: false,
              nivel: 3
            })
          }
        }
      })
      return list;
    },
    getSiblings(splitter, nivel){
      const siblings = [];
      const siblingsIds = [];
      let parentsId;
      splitter.inPorts.forEach(port => {
        if(port.connectedPort){
          parentsId = port.connectedPort.pt_splitter_id;
        }
      });
      const parentSp = this.splitters.find(item => item.sp_id === parentsId);
      parentSp.outPorts.forEach(port => {
        if(port.connectedPort && port.connectedPort.pt_splitter_id !== splitter.sp_id){
          siblingsIds.push(port.connectedPort.pt_splitter_id)
        }
      })
      siblingsIds.forEach(id => {
        const splitter = this.splitters.find(sp => sp.sp_id === id);
        siblings.push({
          sp_id: splitter.sp_id,
          splitter,
          hide: false,
          nivel: nivel
        })
      })
      return siblings;
    },

    getSpilttersConnected(spId, spArray){
      //check if splitter was already added
      let hasOutportConnected = false;
      const findSplitter = spArray.find(item => item.sp_id === spId);
      if(!!findSplitter || !(!!spId)){
        return;
      }
      const splitter = this.splitters.find(splitter => splitter.sp_id === spId);
      let nextSpId;
      splitter.inPorts.forEach(port => {
        if(!!port.connectedPort){
          hasOutportConnected = true;
          const checkSplitterExists = spArray.find(item => item.sp_id === port.connectedPort.pt_splitter_id);
          if(!(!!checkSplitterExists)){
            nextSpId = port.connectedPort.pt_splitter_id;
            spArray.push({
              sp_id: splitter.sp_id,
              splitter,
              hide: false
            });
          }
        }
      });
      if(!hasOutportConnected){
        spArray.push({
          sp_id: splitter.sp_id,
          splitter,
          hide: false
        });
      }
      this.getSpilttersConnected(nextSpId, spArray);
    },
    connectTo(){
      this.isCreateEquipmentFormOpen = true;
    },
    closeConnectTo(){
      this.isCreateEquipmentFormOpen = false;
    },
    changeSelection() {
      document.getElementById("form-splicing-tab").onclick = "";
      this.selectItem(
        this.connectionSameSide.side,
        this.connectionSameSide.item,
        true
      );
      this.showConnectionOrChange = false;
    },

    isSelectedItemEqual(side, item) {
      let originalItem = this[side].selectedConnectionPoint;
      let identifier;

      switch (originalItem.type) {
        case "cable":
          identifier = "cb_uuid";
          break;
        case "tube":
          identifier = "tube_id";
          break;
        case "fiber":
          identifier = "fb_uuid";
          break;
        case "splitter":
          identifier = "sp_uuid";
          break;
        case "port":
          identifier = "pt_uuid";
          break;
      }
      return (
        originalItem.type === item.type &&
        originalItem[identifier] === item[identifier]
      );
    },

    //region Create Connection methods

    creatingConnectionSameSide() {
      let side = this.connectionSameSide.side;

      this.newConnections.originItems.push(this[side].selectedConnectionPoint);
      this.newConnections.destinationItems.push(this.connectionSameSide.item);

      this.setConnectionPointsPreviousConnections(
        "origin",
        this.newConnections.originItems
      );
      this.setConnectionPointsPreviousConnections(
        "origin",
        this.newConnections.destinationItems
      );

      this.setConnectionPointsPreviousConnections(
        "destination",
        this.newConnections.destinationItems
      );
      this.setConnectionPointsPreviousConnections(
        "destination",
        this.newConnections.originItems
      );

      if (this.previousConnectionsToRemove.length > 0) {
        this.showPreviousConnectionsDialog = true;
      } else {
        this.continueCreatingConnections();
      }
    },

    startCreatingConnection() {
      if (
        this.isNewConnectionInSameSplitter(
          this.origin.selectedConnectionPoint,
          this.destination.selectedConnectionPoint
        )
      ) {
        this.$toasted.global.toast_error(
          this.$t("SameSplitterConnectionWarning")
        );
        this.resetSelectedConnectionItems();
      } else {
        this.setNewConnectionsItems();

        this.setConnectionPointsPreviousConnections(
          "origin",
          this.newConnections.originItems
        );
        this.setConnectionPointsPreviousConnections(
          "destination",
          this.newConnections.destinationItems
        );

        if (this.previousConnectionsToRemove.length > 0) {
          if (this.isNewConnectionBetweenSingleConnectionPoints()) {
            this.showPreviousConnectionsDialog = true;
          } else {
            this.showMultipleNewConnectionsDialog = true;
          }
        } else {
          this.continueCreatingConnections();
        }
      }
    },

    isNewConnectionInSameSplitter(origin, destination) {
      let originSplitterId = origin.fb_splitter_id
        ? origin.fb_splitter_id
        : origin.pt_splitter_id;
      let destinationSplitterId = destination.fb_splitter_id
        ? destination.fb_splitter_id
        : destination.pt_splitter_id;

      return (
        (origin.type === "port" &&
          destination.type === "port" &&
          originSplitterId === destinationSplitterId) ||
        (origin.type === "splitter" &&
          destination.type === "splitter" &&
          origin.sp_id === destination.sp_id)
      );
    },

    resetSelectedConnectionItems() {
      this.origin.selectedConnectionPoint = null;
      this.destination.selectedConnectionPoint = null;

      this.newConnections.originItems = [];
      this.newConnections.destinationItems = [];
      this.newConnections.firstSideSelected = null;
      this.previousConnectionsToRemove = [];
      this.connectionSameSide = {
        side: null,
        item: null
      }
    },

    setNewConnectionsItems() {
      let originType = this.getNewConnectionSideType("origin");
      let destinationType = this.getNewConnectionSideType("destination");

      if (originType === destinationType) {
        if (originType === "single") {
          this.newConnections.originItems.push(
            this.origin.selectedConnectionPoint
          );
          this.newConnections.destinationItems.push(
            this.destination.selectedConnectionPoint
          );
        } else {
          this.newConnections.originItems = this.getObjectConnectionPoints(
            this.origin.selectedConnectionPoint,
            "origin"
          );
          this.newConnections.destinationItems = this.getObjectConnectionPoints(
            this.destination.selectedConnectionPoint,
            "destination"
          );
        }
      } else {
        this.setNewConnectionsItemsInSingleToMultipleRelation(
          this.newConnections.firstSideSelected
        );
      }
    },

    setNewConnectionsItemsInSingleToMultipleRelation(side) {
      let oppositeSide = this.getOppositeSide(side);
      if (this.isSingleConnectionPoint(side)) {
        this.newConnections[side + "Items"].push(
          this[side].selectedConnectionPoint
        );
        this.newConnections[oppositeSide + "Items"].push(
          this.getFirstConnectionPointOfSelectedItem(oppositeSide)
        );
      } else {
        this.newConnections[side + "Items"] = this.getObjectConnectionPoints(
          this[side].selectedConnectionPoint,
          side
        );
        let connectionPointsNumber = this.newConnections[side + "Items"].length;
        this.newConnections[
          oppositeSide + "Items"
        ] = this.getConnectionPointNextSiblings(
          oppositeSide,
          connectionPointsNumber
        );
      }
    },

    getNewConnectionSideType(side) {
      if (this.isSingleConnectionPoint(side)) {
        return "single";
      } else {
        return "mulitple";
      }
    },

    getFirstConnectionPointOfSelectedItem(side) {
      let object = this[side].selectedConnectionPoint;
      let connectionPoints = this.getObjectConnectionPoints(object, side);
      return connectionPoints[0];
    },

    getConnectionPointNextSiblings(side, nrOfPossibleConnections) {
      let connectionPoint = this[side].selectedConnectionPoint;
      let parent, connectionPointIdentifier;

      if (this.isFiber(connectionPoint)) {
        parent = this.getFiberCable(side, connectionPoint);
        connectionPointIdentifier = "fb_id";
      } else if (this.isPort(connectionPoint)) {
        parent = this.getPortSplitter(connectionPoint);
        connectionPointIdentifier = "pt_id";
      }

      let parentConnectionPoints = this.getObjectConnectionPoints(parent, side);
      let connectionPointIndex = parentConnectionPoints.findIndex(
        item =>
          item[connectionPointIdentifier] ===
          connectionPoint[connectionPointIdentifier]
      );

      let lastIndex = connectionPointIndex + nrOfPossibleConnections;
      let connectionPoints = [];
      for (let i = connectionPointIndex; i < lastIndex; i++) {
        if (parentConnectionPoints[i]) {
          connectionPoints.push(parentConnectionPoints[i]);
        } else {
          break;
        }
      }
      return connectionPoints;
    },

    getObjectConnectionPoints(object, side) {
      if (this.isCable(object) || object.type === "tube") {
        return object.fibers;
      } else if (this.isSplitter(object)) {
        if (object.hasOwnProperty("ports")) {
          return object.ports;
        } else if (side === "origin") {
          return object.outPorts;
        } else if (side === "destination") {
          return object.inPorts;
        }
      }
      return [];
    },

    isNewConnectionBetweenSingleConnectionPoints() {
      return (
        this.isSingleConnectionPoint("origin") &&
        this.isSingleConnectionPoint("destination")
      );
    },

    isSingleConnectionPoint(side) {
      let selectedItem = this[side].selectedConnectionPoint;
      return selectedItem.type === "fiber" || selectedItem.type === "port";
    },

    setConnectionPointsPreviousConnections(side, connectionPoints) {
      if (connectionPoints) {
        for (const connectionPoint of connectionPoints) {
          this.addSelectedFiberOrPortConnectionIndexToRemovalList(
            side,
            connectionPoint
          );
        }
      }
    },

    addSelectedFiberOrPortConnectionIndexToRemovalList(side, connectionPoint) {
      let oppositeSide = this.getOppositeSide(side);
      let connectionPointType, connectionPointUUID;
      if (this.isFiber(connectionPoint)) {
        connectionPointType = "fiber";
        connectionPointUUID = "fb_uuid";
      } else if (this.isPort(connectionPoint)) {
        connectionPointType = "port";
        connectionPointUUID = "pt_uuid";
      }
      let connectionIndex = this.connections.findIndex(connection => {
        if (
          (this.connectionSameSide.side === side ||
            connection.same_side_connection) &&
          connectionPointType === "fiber"
        ) {
          return (
            (connection[side].type === connectionPointType &&
              connection[side].uuid === connectionPoint[connectionPointUUID]) ||
            (connection[oppositeSide].type === connectionPointType &&
              connection[oppositeSide].uuid ===
                connectionPoint[connectionPointUUID])
          );
        } else {
          return (
            connection[side].type === connectionPointType &&
            connection[side].uuid === connectionPoint[connectionPointUUID]
          );
        }
      });
      if (connectionIndex > -1) {
        this.previousConnectionsToRemove.push(connectionIndex);
      }
    },

    continueCreatingConnections(isTubesConnection = false) {
      const connectionsCopy = [...this.connections];
      this.closePreviousSingleConnectionDialog();
      this.closePreviousMultipleConnectionDialog();

      if (this.previousConnectionsToRemove.length > 0) {
        this.isconnectingTubes = isTubesConnection;
        this.removePreviousConnections();
      }

      for (let i = 0; i < this.newConnections.originItems.length; i++) {
        if (
          this.newConnections.originItems[i] &&
          this.newConnections.destinationItems[i]
        ) {
          let sideOrigin = "origin";
          let sideDestination = this.fiberIsOrigin ? "origin" : "destination";
          if (this.showConnectionOrChange) {
            sideOrigin = this.connectionSameSide.side;
            sideDestination = this.connectionSameSide.side;
          }
          this.createConnection(
            sideOrigin,
            sideDestination,
            this.newConnections.originItems[i],
            this.newConnections.destinationItems[i],
            connectionsCopy
          );
        } else {
          break;
        }
      }

      this.resetSelectedConnectionItems();
      this.updateConnectionsInClosureForm();

    },

    createConnection(sideOrigin, sideDestination, originItem, destinationItem, connectionsCopy) {
      this.addConnectionToSplitter(originItem, destinationItem);
      //if connection is fiber to fiber, check for position and tray in connection
      if(destinationItem.hasOwnProperty('fb_id') && originItem.hasOwnProperty('fb_id')){
        const destinationPosition = this.getDestinationPosition(connectionsCopy, destinationItem);
        const originPosition = this.getOriginPosition(connectionsCopy, originItem);
        if(destinationPosition){
          this.connections.push({
            ...destinationPosition,
            origin: this.getSelectedItemResumedData(sideOrigin, originItem),
            destination: this.getSelectedItemResumedData(
              sideDestination,
              destinationItem
            ),
            mode: this.getNewConnectionMode(
              sideOrigin,
              sideDestination,
              originItem,
              destinationItem
            ),
            same_side_connection: sideOrigin === sideDestination,
          });
        }else if(originPosition){
          this.connections.push({
            ...originPosition,
            origin: this.getSelectedItemResumedData(sideOrigin, originItem),
            destination: this.getSelectedItemResumedData(
              sideDestination,
              destinationItem
            ),
            mode: this.getNewConnectionMode(
              sideOrigin,
              sideDestination,
              originItem,
              destinationItem
            ),
            same_side_connection: sideOrigin === sideDestination,
          });
        }else{
          this.connections.push({
            origin: this.getSelectedItemResumedData(sideOrigin, originItem),
            destination: this.getSelectedItemResumedData(
              sideDestination,
              destinationItem
            ),
            mode: this.getNewConnectionMode(
              sideOrigin,
              sideDestination,
              originItem,
              destinationItem
            ),
            same_side_connection: sideOrigin === sideDestination,
          });
        }
      }else{
        this.connections.push({
          origin: this.getSelectedItemResumedData(sideOrigin, originItem),
          destination: this.getSelectedItemResumedData(
            sideDestination,
            destinationItem
          ),
          mode: this.getNewConnectionMode(
            sideOrigin,
            sideDestination,
            originItem,
            destinationItem
          ),
          same_side_connection: sideOrigin === sideDestination,
        });
      }
      this.createConnectionsArray(_.cloneDeep(this.connections));
      this.showConnectionOrChange = false;
    },
    getDestinationPosition(connectionsCopy, destinationItem){
      const obj = connectionsCopy.find(item => item.destination.id === destinationItem.fb_id)
      return obj;
    },
    getOriginPosition(connectionsCopy, originItem){
      const obj = connectionsCopy.find(item => item.origin.id === originItem.fb_id)
      return obj;
    },
    addConnectionToSplitter(originItem, destinationItem){
      if(originItem.hasOwnProperty('pt_id') && destinationItem.hasOwnProperty('fb_id')){
        //splitter to fiber connection
        this.addConnectionToList(destinationItem, originItem, false);
      }
    },
    getSelectedItemResumedData(side, fiberOrPort) {
      if (this.isFiber(fiberOrPort)) {
        return this.getNewFiberConnectionSide(fiberOrPort, side);
      } else if (this.isPort(fiberOrPort)) {
        return this.getNewPortConnectionSide(fiberOrPort);
      }
    },

    getNewFiberConnectionSide(fiber, side) {
      let connectionSide = {
        type: "fiber",
        id: fiber.fb_id,
        uuid: fiber.fb_uuid,
        parent: {
          type: "cable",
          id: fiber.fb_cable_id
        }
      };

      let cable = this.getFiberCable(side, fiber);
      connectionSide.parent.uuid = cable.cb_uuid;
      return connectionSide;
    },

    getNewPortConnectionSide(port) {
      let connectionSide = {
        type: "port",
        id: port.pt_id,
        uuid: port.pt_uuid,
        parent: {
          type: "splitter",
          id: port.pt_splitter_id
        }
      };

      let splitter = this.getPortSplitter(port);
      connectionSide.parent.uuid = splitter.sp_uuid;
      return connectionSide;
    },

    getNewConnectionMode(
      sideOrigin,
      sideDestination,
      originItem,
      destinationItem
    ) {
      let mode = "CONNECTOR";

      if (this.isFiber(originItem) && this.isFiber(destinationItem)) {
        let originCable = this.getFiberCable(sideOrigin, originItem);
        let destinationCable = this.getFiberCable(
          sideDestination,
          destinationItem
        );

        if (
          originCable.cb_parent_id === destinationCable.cb_parent_id &&
          originItem.fb_nr_in_cable === destinationItem.fb_nr_in_cable
        ) {
          mode = "PASSING";
        } else {
          mode = "SPLICE";
        }
      }
      return mode;
    },

    removePreviousConnections() {
      if(this.isconnectingTubes){
        let newConnectionsToRemove = [];
        const splittersToRemove = [];
        if(this.origin.selectedConnectionPoint.hasOwnProperty('sp_id') || this.destination.selectedConnectionPoint.hasOwnProperty('sp_id')){
          const splitter = this.origin.selectedConnectionPoint.hasOwnProperty('sp_id') ? this.origin.selectedConnectionPoint : this.destination.selectedConnectionPoint;
          const splittersToRemoveList = [];
          this.getSpilttersToRemove(splitter.sp_id, splittersToRemoveList);
          //delete connections from splitters
          if(splittersToRemoveList.length > 0){
            splittersToRemoveList.forEach(item => {
              this.removeConnectionsFromSplitter(splitter, item.sp_id, false)
              //delete splitter from waterfall
              if(item.sp_id !== splitter.sp_id){
                this.splittersList.forEach(listItem => {
                  let indexToRemove = listItem.waterfall.findIndex(obj => obj.sp_id === item.sp_id);
                  if(indexToRemove >= 0){
                    listItem.waterfall.splice(indexToRemove, 1);
                  }
                });
              }
            });
            //update splitter in list
            this.updateSplitterFromList(splitter);
          }
          splittersToRemoveList.forEach(obj =>{
              const id = splittersToRemove.find(item => item === obj.sp_id);
              if(!id){
                splittersToRemove.push(obj.sp_id);
              }
          });
          if(splittersToRemove.length > 0){
            const indexesToRemove = this.removeSplitterButCurrent(splittersToRemove, splitter.sp_id);
            newConnectionsToRemove = [...indexesToRemove]
          }
          //add rest of connection to remove
          this.previousConnectionsToRemove.forEach(index => {
            const indexExists = newConnectionsToRemove.find(item => item === index);
            if(!indexExists){
              newConnectionsToRemove.push(index);
            }
          });
        }else{
          this.previousConnectionsToRemove.forEach(index => {
            const splittersToRemoveList = [];
            if(this.connections[index].origin.type === 'fiber' && this.connections[index].destination.type === 'port'){
              this.getSpilttersToRemove(this.connections[index].destination.parent.id, splittersToRemoveList);
              if(splittersToRemoveList.length > 0){
                splittersToRemoveList.forEach(item => {
                  //delete splitter from waterfall
                  this.splittersList.forEach(listItem => {
                    let indexToRemove = listItem.waterfall.findIndex(obj => obj.sp_id === item.sp_id);
                    if(indexToRemove >= 0){
                      listItem.waterfall.splice(indexToRemove, 1);
                    }
                  });

                });
              }
              splittersToRemoveList.forEach(obj =>{
                const id = splittersToRemove.find(item => item === obj.sp_id);
                if(!id){
                  splittersToRemove.push(obj.sp_id);
                }
              })
            }else{
              newConnectionsToRemove.push(index);
            }
          });
          if(splittersToRemove.length > 0){
            const indexesToRemove = this.removeSplitterInConnections(splittersToRemove);
            newConnectionsToRemove = [...newConnectionsToRemove, ...indexesToRemove]
          }
        }
        let connectionsToRemove = _.uniq(newConnectionsToRemove);
        _.pullAt(this.connections, connectionsToRemove);
        this.previousConnectionsToRemove = [];
        this.isconnectingTubes = false;
      }else{
        let connectionsToRemove = _.uniq(this.previousConnectionsToRemove);
        _.pullAt(this.connections, connectionsToRemove);
        this.previousConnectionsToRemove = [];

      }
      this.createConnectionsArray(_.cloneDeep(this.connections));
    },
    //remove all connections From Splitter
    removeConnectionsFromSplitter(currentSp, spId, deleteAll){
      //if not deleteAll we delete connections from outports of current and all connections from rest of splitters
      if(!deleteAll){
        if(currentSp.sp_id === spId){
          this.splitters.forEach(splitter => {
            if(splitter.sp_id === spId){
              splitter.outPorts.forEach(port => {
                port.connectedPort = null;
                port.connectedFiber = null;
              });
            }
          });
        }else{
          this.splitters.forEach(splitter => {
            if(splitter.sp_id === spId){
              splitter.outPorts.forEach(port => {
                port.connectedPort = null;
                port.connectedFiber = null;
              });
              splitter.inPorts.forEach(port => {
                port.connectedPort = null;
                port.connectedFiber = null;
              });
            }
          });
        }
      }else{

      }

    },
    //remove all splitters but current splitter in connections
    removeSplitterButCurrent(splittersIds, currentSpId){
      const newConnections = this.connections.map((connection, index) => {
        if(connection.origin.parent && connection.destination.parent){
          const isInOrigin = splittersIds.find(id => id === connection.origin.parent.id);
          const isInDestination = splittersIds.find(id => id === connection.destination.parent.id);
          if(isInDestination && isInDestination !== currentSpId){
            return index
          }else if(isInOrigin){
            return index
          }
        }
      });
      return newConnections;
    },
    //remove all splitters from connection
    removeSplitterInConnections(splittersIds){
      const newConnections = this.connections.map((connection, index) => {
        if(connection.origin.parent && connection.destination.parent){
          const isInOrigin = splittersIds.find(id => id === connection.origin.parent.id);
          const isInDestination = splittersIds.find(id => id === connection.destination.parent.id);
          if(isInOrigin || isInDestination){
            return index
          }
        }
      });
      return newConnections;
    },
    cancelNewSingleConnection() {
      this.closePreviousSingleConnectionDialog();
      this.resetSelectedConnectionItems();
    },
    cancelDeleteConnection() {
      this.showDeleteConnectionDialog = false;
      this.resetSelectedConnectionItems();
    },
    confirmDeleteConnections(){
      this.removeConnectionsFromMenu();
      this.showDeleteConnectionDialog = false;
    },
    closePreviousSingleConnectionDialog() {
      this.showPreviousConnectionsDialog = false;
    },

    cancelNewMultipleConnection() {
      this.closePreviousMultipleConnectionDialog();
      this.resetSelectedConnectionItems();
    },

    closePreviousMultipleConnectionDialog() {
      this.showMultipleNewConnectionsDialog = false;
    },

    //endregion
    connectionsShowMenu(itemData){
      itemData.event.preventDefault();
      this.menu.show = false;
      this.menu.x = itemData.event.clientX;
      this.menu.y = itemData.event.clientY;
      this.menu.selected.side = null;
      this.menu.selected.connectionPoints = {origin: {...itemData.origin}, destination: {...itemData.destination}};
      this.menu.selected.state = 'splitter';
      this.$nextTick(() => {
        this.menu.show = true;
      });
    },

    listShowMenu(e, splitter, nivel){
      this.getCollapseOrExpand(splitter);
      this.selectedSplitter = {splitter, nivel};
      this.menu.show = false;
      this.menu.x = e.clientX;
      this.menu.y = e.clientY;
      this.menu.selected.state = 'list';
      this.$nextTick(() => {
        this.menu.show = true;
      });
    },
    getCollapseOrExpand(obj){
      this.splittersList.forEach(item => {
        item.waterfall.forEach(splitter => {
          if(splitter.sp_id === obj.sp_id){
            if(splitter.hide){
              !this.isExpand && (this.isExpand = true);
            }else{
              this.isExpand && (this.isExpand = false);
            }
          }
        })
      })
    },
    showMenu(side, itemData) {
      itemData.event.preventDefault();
      this.menu.show = false;
      this.menu.x = itemData.event.clientX;
      this.menu.y = itemData.event.clientY;
      this.menu.selected.side = side;
      this.menu.selected.connectionPoints = itemData.connectionPoints;
      this.menu.selected.state = itemData.state ? itemData.state : null;
      this.menu.selected.prefix = itemData.prefix ? itemData.prefix : null;
      this.$nextTick(() => {
        this.menu.show = true;
      });
    },

    closeMenu() {
      this.menu.show = false;
      this.menu.selected.side = null;
      this.menu.selected.connectionPoints = null;
      this.menu.selected.state = null;
      this.showConnectionOrChange = false;
    },
    collapseListSplitter(){
      this.splittersList.forEach(item => {
        const isFound = !!item.waterfall.find(splitter => splitter.sp_id === this.selectedSplitter.splitter.sp_id);
        if(isFound){
          item.waterfall.forEach(splitter => {
            splitter.hide = !splitter.hide;
          });
        }
        return false;
      });
    },
    hideListSplitter(){
      let listId;
      this.splittersList.forEach(item => {
        item.waterfall.forEach(splitter => {
          if(splitter.sp_id === this.selectedSplitter.splitter.sp_id){
            listId = item.id;
            return false
          }
        })
      });
      const newList = this.splittersList.filter(item => item.id !== listId);
      this.splittersList = [...newList];
    },
    async removeSplitter(){
      try{
        this.showDeleteSplitterDialog = false;
        //request to delete splitter
        await this.deleteSplitter(this.selectedSplitter.splitter.sp_uuid);
        const splittersToRemove = [];
        this.getSpilttersToRemove(this.selectedSplitter.splitter.sp_id, splittersToRemove);
        //delete splitter connections
        if(splittersToRemove.length > 0){
          this.removeSplitterFromConnections(splittersToRemove);
        }
        // delete splitter from splitters list
        this.removeSplitterFromSplitters(this.selectedSplitter.splitter.sp_id);
        //delete splitter from list
        this.removeSplitterFromList(splittersToRemove);
      }catch(err){
        this.$toasted.global.toast_error(err.response.data.message);
      }
    },
    removeSplitterFromConnections(splittersToRemove){
      this.splitters.forEach(splitter => {
        const splitterFound = splittersToRemove.find(item => item.sp_id === splitter.sp_id);
        if(!splitterFound){
          this.removeSplitterInports(splitter, splittersToRemove);
        }
      });
      splittersToRemove.forEach(item => {
        //remove connections
        this.setConnectionPointsPreviousConnections(
          "origin",
          item.destination
        );
        this.setConnectionPointsPreviousConnections(
          "destination",
          item.origin
        );
        //remove connection from splitter ports
        this.removeConnectionFromPorts(item.sp_id);
      });
      this.removePreviousConnections();
      this.updateConnectionsInClosureForm();
    },
    updateSplitterFromList(splitter){
      const updatedSplitter = this.splitters.find(item => item.sp_id === splitter.sp_id);
      this.splittersList.forEach(item => {
        const waterfallItemIndex = item.waterfall.findIndex(obj => obj.sp_id === splitter.sp_id);
        if(waterfallItemIndex >= 0){
          item.waterfall[waterfallItemIndex].splitter = {...updatedSplitter}
        }
      });

    },
    removeSplitterFromList(splittersToRemove){
      const copy = [...this.splittersList];
      this.deleteFromOutport(copy);
      splittersToRemove.forEach(splitter => {
        copy.forEach(item => {
          let indexToRemove = item.waterfall.findIndex(obj => obj.sp_id === splitter.sp_id);
          if(indexToRemove >= 0){
            item.waterfall.splice(indexToRemove, 1);
          }
        })
      });
      this.splittersList = [...copy];
    },
    deleteFromOutport(splitters){
      if(this.selectedSplitter.nivel > 0){
        splitters.forEach(list => {
          const listFound = list.waterfall.find(item => item.sp_id === this.selectedSplitter.splitter.sp_id);
          if(listFound){
            list.waterfall[this.selectedSplitter.nivel - 1].splitter.outPorts.forEach(port => {
              if(port.connectedPort && (port.connectedPort.pt_splitter_id === this.selectedSplitter.splitter.sp_id)){
                port.connectedPort = null;
              }
            })
          }
        });
      }
    },
    removeSplitterFromSplitters(spId){
      let indexToRemove = this.splitters.findIndex(obj => obj.sp_id === spId);
      this.splitters.splice(indexToRemove, 1);
    },
    removeConnectionsFromMenu() {
      const obj = {...this.menu.selected}
      if(obj.state === 'splitter'){
        const splittersToRemove = [];
        this.getSpilttersToRemove(obj.connectionPoints.destination.connectionPoints[0].pt_splitter_id, splittersToRemove);
        if(splittersToRemove.length > 0){
          this.splitters.forEach(splitter => {
            const splitterFound = splittersToRemove.find(item => item.sp_id === splitter.sp_id);
            if(!splitterFound){
              this.removeSplitterInports(splitter, splittersToRemove);
            }
          });
          splittersToRemove.forEach(item => {
            //remove connections
            this.setConnectionPointsPreviousConnections(
              "origin",
              item.destination
            );
            this.setConnectionPointsPreviousConnections(
              "destination",
              item.origin
            );
            //remove connection from splitter ports
            this.removeConnectionFromPorts(item.sp_id);
          });
          if(this.splittersList.length > 0){
            //delete splitters from list
            splittersToRemove.forEach(splitter => {
              this.splittersList.forEach(item => {
                let indexToRemove = item.waterfall.findIndex(obj => obj.sp_id === splitter.sp_id);
                if(indexToRemove >= 0){
                  item.waterfall.forEach(obj => {
                    obj.splitter.outPorts.forEach(port => {
                      port.connectedPort && (port.connectedPort = null);
                    })
                  })
                  item.waterfall.splice(indexToRemove, 1);
                }
              })
            });
          }
        }
        this.removePreviousConnections();
        this.closeMenu();
        this.updateConnectionsInClosureForm();
      }else{
        const sp = this.getSplitter(obj.connectionPoints[0].fb_id);
        const numberConnections = this.getInportConnections(sp);
        if(numberConnections === 1 && obj.side === 'origin'){
          this.$toasted.global.toast_error('Cannot delete only connection to splitter');
          return;
        }
        obj.connectionPoints.forEach(connection => {
          this.removeFiberFromPort(connection.fb_id);
        });
        this.setConnectionPointsPreviousConnections(
          this.menu.selected.side,
          this.menu.selected.connectionPoints
        );
        if(this.splittersList.length > 0){
          //remove connection from list of splitters
          this.removeConnectionFromList(obj.connectionPoints[0].fb_id);
        }
        this.removePreviousConnections();
        this.closeMenu();
        this.updateConnectionsInClosureForm();
      }
    },
    getSplitter(spId){
      let sp;
      this.splitters.forEach(splitter => {
        splitter.inPorts.forEach(port => {
          if(port.connectedFiber && port.connectedFiber.fb_id === spId){
            sp = {...splitter};
            return false;
          }
        })
      });
      return sp;
    },
    getInportConnections(sp){
      let count = 0;
      if(sp){
        sp.inPorts.forEach(port => {
          if(port.connectedFiber){
            count = count + 1;
          }
        });
      }
      return count;
    },
    removeFiberFromPort(fiberId){
      let foundInInports =  false;
      this.splitters.forEach(splitter => {
        splitter.inPorts.forEach(port => {
          if(port.connectedFiber && (port.connectedFiber.fb_id === fiberId)){
            foundInInports = true;
            port.connectedFiber = null;
            return false;
          }
        });
        if(!foundInInports){
          splitter.outPorts.forEach(port => {
            if(port.connectedFiber && (port.connectedFiber.fb_id === fiberId)){
              port.connectedFiber = null;
              return false;
            }
          });
        }
      })
    },
    removeSplitterInports(splitter, splittersToRemove){
      this.splitters.forEach(item => {
        if(item.sp_id === splitter.sp_id){
          splittersToRemove.forEach(splitterToRemove => {
            item.outPorts.forEach(port => {
              if(port.connectedPort && (splitterToRemove.sp_id === port.connectedPort.pt_splitter_id)){
                port.connectedPort = null;
              }
            })
          })
        }
      })
    },
    getSpilttersToRemove(spId, spArray) {
      const findSplitter = spArray.find(item => item.sp_id === spId);
      if (findSplitter || !spId) {
        return;
      }

      const splitter = this.splitters.find(splitter => splitter.sp_id === spId);

      spArray.push({
        sp_id: splitter.sp_id,
        origin: [...splitter.inPorts],
        destination: [...splitter.outPorts]
      });

      splitter.outPorts.forEach(port => {
        if (port.connectedPort) {
          const connectedSplitterId = port.connectedPort.pt_splitter_id;
          this.getSpilttersToRemove(connectedSplitterId, spArray);
        }
      });
    },
    removeConnectionFromPorts(splitterId){
      this.splitters.forEach(splitter => {
        if(splitter.sp_id === splitterId){
          splitter.inPorts.forEach(port => {
            port.connectedFiber = null;
            port.connectedPort = null;
          });
          splitter.outPorts.forEach(port => {
            port.connectedFiber = null;
            port.connectedPort = null;
          });
        }
      });
    },
    updateConnectionsInClosureForm() {
      this.updateCounter++;
      this.$emit("update-connections", this.connections);
      this.$emit("set-initial-connections", this.initialSplicingConnections)
    },

    resetObjectsList() {
      this.origin.cables = null;
      this.destination.cables = null;
      this.splitters = null;

      this.origin.selectedObjects = [];
      this.destination.selectedObjects = [];

      this.updateCounter++;
    },
    openTable(){
      let routeData = this.$router.resolve({
        name: "splicing",
      });
      window.open(routeData.href, '_blank');
    },
    inspectConnection() {
      if(this.menu.selected.state === 'splitter'){
        this.inspectSplitterConnection();
        return
      }
      let side = this.menu.selected.side;
      let connectionPoint = this.menu.selected.connectionPoints[0];
      let uuid;
      if (this.isFiber(connectionPoint)) {
        uuid = connectionPoint.fb_uuid;
      } else if (this.isPort(connectionPoint)) {
        uuid = connectionPoint.pt_uuid;
      }
      if (uuid) {
        let connection = this.connections.find(
          item => item[side].uuid === uuid
        );
        if (connection) {
          this.makeConnectionSideVisible(
            connection,
            this.getOppositeSide(side)
          );
        }
      }
      this.closeMenu();
    },
    inspectSplitterConnection() {
      let connectionPoint = this.menu.selected.connectionPoints.destination.connectionPoints[0];
      const options = [...this.splitters];
      let selectedObject = this['destination'].selectedObjects.find(
        obj => obj.sp_id === connectionPoint.pt_splitter_id
      );
      if (!selectedObject) {
        const objectToAdd = options.find(
          option => option.sp_id === connectionPoint.pt_splitter_id
        );
        this['destination'].selectedObjects.push(objectToAdd);
        const connectedSplitters = this.hasSplitterConnected(objectToAdd);
        if(connectedSplitters.length > 0){
          connectedSplitters.forEach(splitter => {
            const objectsSelected = this['origin'].selectedObjects.find(
              obj => obj.sp_id === splitter.pt_splitter_id
            );
            if(!objectsSelected){
              const splitterToAdd = options.find(
                option => option.sp_id === splitter.pt_splitter_id
              );
              this['origin'].selectedObjects.push(splitterToAdd);
            };
          });
        };
      };
      this.closeMenu();
    },
    hasSplitterConnected(obj){
      const splittersList = [];
      obj.outPorts.forEach(port => {
        if(!!port.connectedPort){
          splittersList.push(port.connectedPort);
        }
      });
      return splittersList;
    },
    makeConnectionSideVisible(connection, side) {
      let options, identifier;
      let isSplitter = false;
      if (connection[side].parent.type === "cable") {
        identifier = "cb_uuid";
        options = this[side].cables;
      } else if (connection[side].parent.type === "splitter") {
        identifier = "sp_uuid";
        options = this.splitters;
        isSplitter = true;
      }
      let selectedObject = this[side].selectedObjects.find(
        item => item[identifier] === connection[side].parent.uuid
      );
      if (!selectedObject) {
        let objectToAdd = options.find(
          option => option[identifier] === connection[side].parent.uuid
        );
        if(isSplitter){
          this[this.getOppositeSide(side)].selectedObjects.push(objectToAdd);
        }else{
          this[side].selectedObjects.push(objectToAdd);
        }
      }
    },

    isCable(object) {
      return object && object.hasOwnProperty("cb_id");
    },

    isSplitter(object) {
      return object && object.hasOwnProperty("sp_uuid");
    },

    isFiber(object) {
      return object && object.hasOwnProperty("fb_uuid");
    },

    isPort(object) {
      return object && object.hasOwnProperty("pt_uuid");
    },

    getPortSplitter(port) {
      return this.splitters.find(
        splitter => splitter.sp_id === port.pt_splitter_id
      );
    },

    getFiberCable(side, fiber) {
      return this[side].cables.find(cable => cable.cb_id === fiber.fb_cable_id);
    },

    getOppositeSide(side) {
      if (side === "origin") {
        return "destination";
      } else if (side === "destination") {
        return "origin";
      }
    },

    showCircuit() {
      let connectFiber = this.menu.selected.connectionPoints[0];
      if (
        this.menu.selected.prefix === "pt" &&
        !!this.menu.selected.connectionPoints[0].connectedPort &&
        !this.menu.selected.connectionPoints[0].connectedFiber
      ) {
        connectFiber = this.getConnectionPointsSplitters(
          this.menu.selected.connectionPoints[0].connectedPort
        );
      }
      if (connectFiber) {
        bus.$emit("show-fiber-circuit", {
          object: connectFiber,
          prefix: this.menu.selected.prefix
        });
      }
    },

    getConnectionPointsSplitters(connected) {
      for (let splitter of this.splitters) {
        let port;
        let portConnected;
        if (connected.pt_type === "out") {
          port = splitter.outPorts.find(
            port => port.pt_uuid === connected.pt_uuid
          );
          if (port) {
            portConnected = splitter.inPorts.find(
              port => port.pt_uuid === port.pt_uuid
            );
          }
        } else if (connected.pt_type === "in") {
          portConnected = splitter.inPorts.find(
            port => port.pt_uuid === connected.pt_uuid
          );
        }

        if (portConnected) {
          if (portConnected.connectedPort) {
            this.getConnectionPointsSplitters(portConnected.connectedPort);
          } else if (portConnected.connectedFiber) {
            return portConnected;
          }
        }
      }
    },

    switchVisibleData: _.debounce(function() {
      this.visibleData = this.visibleData === 'splicing' ? 'insidePlan' : 'splicing';
      localStorage.setItem("visibleData", this.visibleData);
    }, 300),

    getOriginObject(object, type, parentType) {
      let obj;
      if (type === "fiber") {
        obj = {
          originFiberID: object.fiber.fb_id,
          originConnectionType: type,
          originMaterial: object.cable.cb_material_id,
          originLabel: object.fiber.fb_label ? object.fiber.fb_label : `CB:${object.fiber.fb_cable_id}`,
          originTubeColor: this.getTubeColor(object.fiber.fb_nr_tube),
          originTubeId: object.fiber.fb_nr_tube,
          originFiberColor: this.getFiberColor(object.fiber.fb_color),
          originIdentificators: `T${object.fiber.fb_nr_tube}/F${object.fiber.fb_nr_in_tube}`,
          originParentType: parentType.charAt(0).toUpperCase() + parentType.slice(1),
          originFiberLogicalType: object.fiber.fb_logical_type,
          originFiberComment: object.fiber.fb_comment,
        }
      } else {
        obj = {
          originConnectionType: type,
          originMaterial: object.splitter.sp_material_id,
          originLabel: object.splitter.sp_label ? object.splitter.sp_label : `SP:${object.splitter.sp_id}`,
          originIdentificators: object.port.connectedFiber ?
          `T${object.port.connectedFiber.fb_nr_tube}/F${object.port.connectedFiber.fb_nr_in_tube}`:
          `P${object.port.connectedPort.pt_nr_in_splitter}`
        }
      }
      return obj;
    },

    getDestinationObject(object, type, parentType) {
      let obj;
      if (type === "fiber") {
        obj = {
          destinationFiberID: object.fiber.fb_id,
          destinationConnectionType: type,
          destinationMaterial: object.cable.cb_material_id,
          destinationLabel: object.fiber.fb_label ? object.fiber.fb_label : `CB:${object.fiber.fb_cable_id}`,
          destinationTubeColor: this.getTubeColor(object.fiber.fb_nr_tube),
          destinationTubeId: object.fiber.fb_nr_tube,
          destinationFiberColor: this.getFiberColor(object.fiber.fb_color),
          destinationIdentificators: `T${object.fiber.fb_nr_tube}/F${object.fiber.fb_nr_in_tube}`,
          destinationParentType: parentType.charAt(0).toUpperCase() + parentType.slice(1),
          destinationFiberLogicalType: object.fiber.fb_logical_type,
          destinationFiberComment: object.fiber.fb_comment,
        }
      } else {
        obj = {
          destinationConnectionType: type,
          destinationMaterial: object.splitter.sp_material_id,
          destinationLabel: object.splitter.sp_label ? object.splitter.sp_label : `SP:${object.splitter.sp_id}`,
          destinationIdentificators: object.port.connectedFiber ?
          `T${object.port.connectedFiber.fb_nr_tube}/F${object.port.connectedFiber.fb_nr_in_tube}`:
          `P${object.port.connectedPort.pt_nr_in_splitter}`
        }
      }
      return obj;
    },

    getFiberColor(colorId){
      let finalColor;
      this.fiberColours.forEach(color => {
        if(color.code === String(colorId)){
          finalColor = color.libelle;
        }
      })
      return finalColor;
    },

    getTubeColor(nr) {
      let colorNr = nr % 12;
      if (colorNr === 0) {
        colorNr = 12;
      }
      switch (colorNr) {
        case 1:
          return "#0070C0";
        case 2:
          return "#FD8408";
        case 3:
          return "#00B050";
        case 4:
          return "#974706";
        case 5:
          return "#A6A6A6";
        case 6:
          return "#FFFFFF";
        case 7:
          return "#FF0000";
        case 8:
          return "#000000";
        case 9:
          return "#FFFF00";
        case 10:
          return "#CE1C93";
        case 11:
          return "#FBA5A3";
        case 12:
          return "#7AD3F2";
      }
    },

    async getClosureCassettes() {
      if(this.objectEndpoint === 'closures'){
        try {
          this.cassettes = await this.fetchClosureCassettes('filter[ct_closure_id]=' + this.objectId);
        } catch(err){
          console.error(err);
        } finally {
        }
      }
    },

    addOriginFibersToFibersAndPortsList() {
      if (this.origin.cables) {
        this.origin.cables.forEach((cable) => {
          cable.fibers.forEach((fiber) => {
            this.fibersPortsCache['fiber'][fiber.fb_id] = {
              fiber,
              cable
            }
          })
        })
      }
    },

    addDestinationFibersToFibersAndPortsList() {
      if (this.destination.cables) {
        this.destination.cables.forEach((cable) => {
          cable.fibers.forEach((fiber) => {
            this.fibersPortsCache['fiber'][fiber.fb_id] = {
              fiber,
              cable
            }
          })
        })
      }
    },

    addSplittersPortsToFibersAndPortsList() {
      if (this.splitters.inPorts) {
        this.splitters.forEach((splitter) => {
          splitter.inPorts.forEach((port) => {
            this.fibersPortsCache['port'][port.pt_id] = {
              port,
              splitter
            }
          })
        })
      }
      if (this.splitters.outPorts) {
        this.splitters.forEach((splitter) => {
          splitter.outPorts.forEach((port) => {
            this.fibersPortsCache['port'][port.pt_id] = {
              port,
              splitter
            }
          })
        })
      }
    },

    setSplitterData(splitter) {
      this.selectedSplitterData = splitter;
    },
    startEditingItem() {
      this.editionFormIsOpen = true;
    },
    closeItemEdition() {
      this.editionFormIsOpen = false;
    },
    async editItem(updatedItem) {
      let updatedItemLabel = updatedItem.sp_label;
      try {
        await this.updateSplitter({
          sp_uuid: this.selectedSplitterData.sp_uuid,
          sp_label: updatedItemLabel
        });
      let optionsAux = this.splitters;
      optionsAux.forEach( splitter => {
        if(splitter.sp_label === this.selectedSplitterData.sp_label){
          splitter.sp_label = updatedItemLabel
        }
      });
      this.objectSet={orig_label: this.selectedSplitterData.sp_label, update_label:updatedItemLabel};
        this.splittersList.forEach(object => {
        object.waterfall.forEach(splitter => {
          if(splitter.splitter.sp_label === this.selectedSplitterData.sp_label) {
            splitter.splitter.sp_label = updatedItemLabel;
          }
        });
      });
      } catch (e) {
        console.log(e);
      }
    },

    async saveCassette(data) {
      try {
        this.isLoading = true;
        let dataToSend = this.getCassetteDataToSend(data);

        if (this.action === "create") {
          await this.createCassette(dataToSend);
          this.getClosureCassettes();
          this.getAllConnections();
        } else {
          await this.editCassette(dataToSend);
          let cassetteIndex = this.cassettes.findIndex(ct => ct.ct_id === this.objectToEdit.ct_id);
          this.cassettes[cassetteIndex].ct_label = data.label;
        }

      } catch (error) {
        console.error(error);
        this.$toasted.global.toast_error(error.data);
      } finally {
        this.isLoading = false;
      }
      this.closeCassetteForm();
    },

    getCassetteDataToSend(cassette) {
      return [{
        label: cassette.label,
        material_id: this.objectToEdit.ct_tmaterial_id,
        capacity: this.objectToEdit.ct_capacity,
        closure_id: this.objectId,
        type: this.objectToEdit.ct_type,
        id: this.objectToEdit.ct_id,
        uuid: this.objectToEdit.ct_uuid,
      }];
    },

    getCassetteDataToSend(cassette) {
      let cassetteMaterialInfo = this.cassetteMaterialsOptions.find(ct => ct.sm_id === cassette.material_id);
      let dataToSend = {};
      if (this.action === 'create') {
        dataToSend = {
          label: cassette.label,
          material_id: cassette.material_id,
          capacity: cassetteMaterialInfo.family_specific_fields.smct_capacity,
          closure_id: this.objectId,
          type: this.cassetteType,
        }
      } else {
        dataToSend = {
          label: cassette.label,
          material_id: this.objectToEdit.ct_material_id,
          capacity: this.objectToEdit.ct_capacity,
          closure_id: this.objectId,
          type: this.objectToEdit.ct_type,
          id: this.objectToEdit.ct_id,
          uuid: this.objectToEdit.ct_uuid,
        }
      }
      return [dataToSend];
    },

    updateLogicalSplicingConnections(connections) {
      this.connections = _.cloneDeep(connections);
      this.updateConnectionsInClosureForm();
    }
  },
};
</script>

<style>
#form-splicing-tab table {
  width: 100%;
  border-collapse: collapse;
}
#form-splicing-tab .tube-container {
  padding: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 10px;
}

.object-label {
  width: 100%;
}
.splicing-origin-table{
  padding-left: 15px;
  position: relative;
}
.splicing-destination-table{
  padding-right: 15px;
  position: relative;
}
.connections-table-container{
  margin-top: 5px;
  position: absolute;
  z-index: 999;
}
.splicing-diagram-origin-container, .splicing-diagram-destination-container{
  max-width: 226px !important;
}

.fadeSplicing-enter-active {
  transition: all 0.3s;
  transition-delay: 0.8s;
}
.fadeSplicing-leave-active {
  transition: all 0.3s;
}
.fadeSplicing-enter,
.fadeSplicing-leave-to {
  /* transform: translateY(-20px); */
  opacity: 0;
}

.fadeInsidePlan-enter-active {
  transition: all 0.3s;
  transition-delay: 0.8s;
}
.fadeInsidePlan-leave-active {
  transition: all 0.3s;
}
.fadeInsidePlan-enter,
.fadeInsidePlan-leave-to {
  /* transform: translateY(-20px); */
  opacity: 0;
}

.splicing-diagram-container {
  z-index: 998;
}

.inside-plan-container {
  height: 100%;
  overflow: auto;
  position: relative;
  justify-content: center;
  flex-direction: row;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/Splicing/SplicingTab.vue